import { ModelNamespaces, PermissionModel, UserActions } from "@granular/authz";

/**
 * Helper function to navigate to Classic Insights in a new tab or switch to
 * it if it's already open.
 *
 * So, what's going on here? The idea is that when the user wants to navigate
 * back to Classic Insights, they will trigger this function which will first
 * open a new blank tab with the name "classic-insights-browser-tab". If that
 * tab is indeed blank then it will take them to Classic. But the next time
 * they click on this same button, and here's the magic, it will not open a new
 * tab, but switch to the previously opened one, if it's still alive of course,
 * otherwise it'll just open a new tab again.
 */
export const goToClassicInsights = (url: URL) => {
  const isFirefoxBrowser = navigator.userAgent.includes("Firefox");
  const classicInsightsTabName = "classic-insights-browser-tab";

  const classicInsightsTabRef = window.open("", classicInsightsTabName);

  if (isFirefoxBrowser) {
    classicInsightsTabRef?.focus();
  }

  if (classicInsightsTabRef?.location.href === "about:blank") {
    window.open(url, classicInsightsTabName);
  }
};

/**
 * Check if a user has permission to access a sidebar item.
 *
 * Accepts either:
 * - A key string from package.json metadata (e.g. "app_map") for apps that
 *   are registered as keys in ModelNamespaces.Fabric3.
 * - A resolved PermissionModel value (e.g. ModelNamespaces.Fabric3.app_trials_planning)
 *   for apps whose package.json key is NOT registered in ModelNamespaces.Fabric3.
 * When undefined, access is allowed by default.
 * Strings that are neither a registered key nor a valid model value are denied fail-closed.
 *
 * NOTE: For explicit model checks in code (e.g. Homepage), prefer passing
 * ModelNamespaces.Fabric3.* resolved values to avoid key-registration gaps.
 *
 * @param modelName - Permission model key, resolved value, or undefined to allow access
 * @param userCan - Function to check user permissions
 * @returns boolean indicating if user has access
 */
export const checkAppPermissions = (
  modelName: string | undefined,
  userCan: (action: UserActions, model: PermissionModel) => boolean,
): boolean => {
  if (!modelName) return true;

  // Key-string path — for apps whose package.json permissionModel key matches
  // a registered key in ModelNamespaces.Fabric3 (e.g. "app_map").
  if (modelName in ModelNamespaces.Fabric3) {
    return userCan(
      "view",
      ModelNamespaces.Fabric3[
        modelName as keyof typeof ModelNamespaces.Fabric3
      ],
    );
  }

  // Resolved PermissionModel value path — for callers passing ModelNamespaces.Fabric3.* directly.
  const validValues = Object.values(ModelNamespaces.Fabric3) as string[];
  if (validValues.includes(modelName)) {
    return userCan("view", modelName as PermissionModel);
  }

  // Unknown string — fail closed without calling userCan.
  return false;
};
